import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../../common/NavBar';
import { SiteContext } from '../../../../context/SiteContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStopwatch } from '@fortawesome/free-solid-svg-icons/faStopwatch';
import { RenewalsApi } from '../../../../api/RenewalsApi';
import {
  RenewalCredentials,
  RenewalDetails,
  PersonalDetailType,
  VehicleState
} from '../../../common/CommonTypes';
import ReactPlayer from 'react-player';
import { Journey, PageAbsolutePath } from '../../../../consts/Common';
import './LandingPage.scss';
import WhySection from './WhySection';
import PageHeader from '../../../common/PageHeader';
import Login, { LoginErrors } from '../../../common/Login/Login';
import AnyQuestionsSection from './AnyQuestionsSection';
import { CommonValidation } from '../../../common/CommonValidation/commonValidation';
import { SelectInputHelper } from '../../../common/SelectInput/SelectInput';
import LoadingContainer from '../../../common/LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../../../common/HelperMethods';
import { blankVehicle } from '../../../testData/journeyHelper';

type RenewalLandingPageProps = {
  callCenterVrn?: string;
  expAuthId?: string;
  campaignId?: string;
};

const initialRenewalsJourneyState = {
  personalDetail: {
    title: SelectInputHelper.NullValue(),
    firstName: '',
    surname: '',
    dateOfBirth: undefined,
    email: '',
    confirmEmail: '',
    phone: '',
    addressDetails: {
      addressLine1: '',
      addressLine2: '',
      townOrCity: '',
      region: '',
      country: '',
      postCode: ''
    }
  },
  vehicle: {
    vehicleDetails: blankVehicle,
    currentMileage: null,
    vehiclePurchasePrice: 0,
    vehicleDeliveryDate: '',
    salesId: '',
    dealerReference: '',
    deliveryDateEstimated: false
  },
  dealerCode: '',
  token: '',
  policyStartDate: undefined as Date | undefined,
  promoCode: ''
};

const RenewalLandingPage = ({
  callCenterVrn,
  expAuthId,
  campaignId
}: RenewalLandingPageProps): JSX.Element => {
  const [errors, setErrors] = useState<LoginErrors>({});

  const [loading, setLoading] = useState(false);

  const [isValid, setIsValid] = useState(false);
  const navigate = useNavigate();
  const { site, setSite } = useContext(SiteContext);

  const cms = site.content.landingPage;

  const [dealerCode, setDealerCode] = useState(
    initialRenewalsJourneyState.dealerCode
  );

  const [policyStartDate, setPolicyStartDate] = useState<Date | undefined>(
    initialRenewalsJourneyState.policyStartDate
  );

  const [token, setToken] = useState(initialRenewalsJourneyState.token);

  const [personalDetail, setPersonalDetails] = useState<PersonalDetailType>(
    initialRenewalsJourneyState.personalDetail
  );
  const [vehicle, setVehicle] = useState<VehicleState>(
    initialRenewalsJourneyState.vehicle
  );

  const [isLateRenewal, setIsLateRenewal] = useState(false);

  const [promoCode, setPromoCode] = useState(
    initialRenewalsJourneyState.promoCode
  );

  const { t } = useTranslation();

  const recordId = site.recordId ?? '';

  console.log('countryCode-' + site.countryCode);

  useEffect(() => {
    const renewalSourceSystem = site.renewalSourceSystem;
    if (
      CommonValidation.isNotEmpty(recordId) &&
      CommonValidation.isNotEmpty(callCenterVrn)
    ) {
      const getRenewals = async () => {
        const controller = new AbortController();

        const successCallback = (data: RenewalDetails) => {
          const vrn = data.vehicleDetail?.vrn;
          if (vrn && callCenterVrn?.toUpperCase() === vrn.toUpperCase()) {
            data.vehicleDetail.errorOrigin = 'vrn';
            data.vehicleDetail.errorMessage = t(
              'common-error-msg.api-error-msg.renewal-recordid-error'
            );
            setDetails(data);
            setLoading(false);
          } else {
            const path = setCountryCode(
              PageAbsolutePath.Error,
              site.countryCode
            );
            navigate(path, {
              state: {
                error: t('landing-page.vehicle-registration-not-match-error')
              }
            });
          }
        };

        const failureCallback = () => {
          const path = setCountryCode(PageAbsolutePath.Error, site.countryCode);
          navigate(path, {
            state: {
              error: t('landing-page.record-not-found-error')
            }
          });

          setIsValid(false);
          setLoading(false);
        };

        setLoading(true);

        new RenewalsApi().getRenewalDetailsFromRecordId(
          recordId,
          callCenterVrn || '',
          renewalSourceSystem || '',
          expAuthId || '',
          campaignId || '0',
          site.user.token,
          successCallback,
          failureCallback,
          controller.signal
        );

        return () => {
          controller.abort();
        };
      };

      getRenewals();
    }
  }, [recordId, callCenterVrn]);

  const validateCredentials = (login: string, password: string) => {
    const controller = new AbortController();

    const successCallback = (data: RenewalDetails) => {
      data.vehicleDetail.errorOrigin = 'vrn';
      data.vehicleDetail.errorMessage = t(
        'common-error-msg.api-error-msg.renewal-vreg-error'
      );
      setDetails(data);
      setLoading(false);
    };

    const failureCallback = () => {
      const newError = {
        vrnError: '',
        passwordError: '',
        invalidCredentialsError: t('landing-page.customer-invalid-credential')
      };
      setErrors(newError);
      setIsValid(false);
      setLoading(false);
    };

    let vin = '';
    let vrn = '';

    if (cms.useVinInput) {
      vin = login;
    } else {
      vrn = login;
    }

    const credentials: RenewalCredentials = { vrn, vin, password };

    setLoading(true);

    new RenewalsApi().getRenewalDetails(
      credentials,
      site.user.token,
      successCallback,
      failureCallback,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const setDetails = (data: RenewalDetails) => {
    setToken(data.token);
    setDealerCode(data.dealerCode);
    setPersonalDetails(data.customerDetail);
    setPolicyStartDate(data.policyStartDate);

    setPromoCode(data.promoCode);

    if (data.dealerCode) {
      const user = { ...site.user, dealerCode: data.dealerCode };

      setSite({
        ...site,
        user: user
      });
    }

    const deliveryDate = JSON.stringify({
      value: data.vehicleDeliveryDate,
      utc: '0'
    });

    if (
      data.vehicleDetail.registrationDate === '' ||
      data.vehicleDetail.registrationDate === null
    ) {
      data.vehicleDetail.errorOrigin = 'vrn';
      data.vehicleDetail.errorMessage = t(
        'common-error-msg.api-error-msg.renewal-vreg-error'
      );
    }

    setVehicle({
      ...vehicle,
      vehicleDetails: data.vehicleDetail,
      vehicleDeliveryDate: deliveryDate
    });

    setIsLateRenewal(data.isLateRenewal);

    const isRenewalValid =
      !data.isRenewalPastExpiry || data.isRenewalExpiryApproved;

    setIsValid(isRenewalValid);

    if (!isRenewalValid) {
      const path = setCountryCode(PageAbsolutePath.Error, site.countryCode);

      navigate(path, {
        state: {
          error: t('landing-page.renewal-expiry-error')
        }
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getPage = (): string => {
    if (site.journeyType === Journey.CallCentre) {
      return isLateRenewal === true
        ? setCountryCode(
            PageAbsolutePath.Renewals.LateRenewalAcceptance,
            site.countryCode
          )
        : setCountryCode(
            PageAbsolutePath.Renewals.VehicleDetails,
            site.countryCode
          );
    } else if (isLateRenewal === true) {
      return setCountryCode(
        PageAbsolutePath.Renewals.LateRenewalAcceptance,
        site.countryCode
      );
    } else {
      return setCountryCode(
        PageAbsolutePath.Renewals.PersonalDetails,
        site.countryCode
      );
    }
  };

  useEffect(() => {
    if (isValid === true) {
      navigate(getPage(), {
        state: {
          personalDetail: personalDetail,
          vehicle: vehicle,
          dealerCode: dealerCode,
          token: token,
          policyStartDate: policyStartDate,
          isLateRenewal: isLateRenewal,
          promoCode: promoCode
        }
      });
    }
  }, [isValid]);

  const onClickLogin = (vreg: string, password: string) => {
    validateCredentials(vreg, password);
  };

  const showMedia = () => {
    if (CommonValidation.isNotEmpty(cms.videoUrl)) {
      return (
        <ReactPlayer
          data-testid='video-player'
          className='react-player'
          controls
          url={cms.videoUrl}
        />
      );
    } else if (CommonValidation.isNotEmpty(cms.wistiaEmbedUrl)) {
      return <div dangerouslySetInnerHTML={{ __html: cms.wistiaEmbedUrl }} />;
    } else {
      return (
        <img
          data-testid='alt-image'
          className='alt-image'
          src={cms.videoAlternativeImageUrl}
          alt='alternative image'
        />
      );
    }
  };

  return (
    <div className='flex-column'>
      <LoadingContainer
        loading={site.journeyType !== Journey.Renewals}
        message={t('loading-placeholder.loading-text')}>
        <NavBar />
        <div className='flex-row mobile-column landing-page--top-section display-block'>
          <div className='flex-column landing-page--intro-wrapper'>
            <div className='landing-page--intro m2-bottom'>
              <div className='page-header-line' />
              <PageHeader
                title={cms.title}
                isRenewalLandingPage={true}
                description={cms.description}
                information={cms.information}
                warning={cms.warning}
                descriptionClass='p1 extra-line-height lp-header-detail'
              />
              <div>
                <Login
                  showEmail={false}
                  loginErrors={errors}
                  loading={loading}
                  onClickLogin={onClickLogin}
                  useVinInput={cms.useVinInput}
                />
                <p className='p4'>
                  <FontAwesomeIcon icon={faStopwatch} className='m05-right' />
                  {cms.journeyTimeNote}
                </p>
              </div>
            </div>
          </div>
          <div className='flex-column'>
            <img
              className='landing-image'
              src={cms.backgroundImageUrl}
              alt='landing page image'
            />
          </div>
        </div>
        <WhySection cms={cms.whySection} showWhyTabs={true} />
        <div className='mobile-column'>
          <div className='product-video-panel'>
            <div className='product-video-details player-wrapper cms-renewal-video-section'>
              {showMedia()}
            </div>
          </div>
        </div>
        <div className='mobile-column'>
          <AnyQuestionsSection cms={cms.anyQuestionsSection} />
        </div>
      </LoadingContainer>
    </div>
  );
};

export default RenewalLandingPage;
